import React, { useEffect, useState } from "react";
import { Link2 } from "lucide-react";
import { formatMelbourneDateTime } from "../../utils/melbourneTime";

// Notes about the same seller typed somewhere else in the CRM - the appraisal,
// the Database row, the Seller Lead or the contact - shown read-only next to a
// screen's own notes, so the whole history is there wherever the seller is
// opened (Genneva Smarrelli, CRM-2026-00106, Monday 13243277727).

const SOURCE_NAMES = {
  property: "Appraisal / Listing",
  database: "Database",
  seller_lead: "Seller Lead",
  contact: "Contact",
};

export const linkedSourceLabel = (n) => {
  const where = SOURCE_NAMES[n?.source] || "CRM";
  return n?.source_label ? `${where} · ${n.source_label}` : where;
};

// Linked notes in the {body, created_at, author} shape the AI summary reads.
export const linkedToSummaryNotes = (linked = []) =>
  (linked || []).map((n) => ({
    body: n.content,
    created_at: n.created_at,
    author: n.author_name || "",
  }));

/**
 * Load the linked notes for the record on screen. `anchor` is
 * { property | contact | record | lead, from }; nothing loads until it names a
 * record. `refreshKey` reloads (e.g. after the screen saves a note). Loaded on
 * demand, like AgentNotesSummary, so the panels' own test suites that stub only
 * specific src/api modules do not pull the API client in. Failures just show
 * nothing: the screen's own notes are never held up.
 */
export function useLinkedNotes(anchor = {}, refreshKey = 0) {
  const { property, contact, record, lead, from } = anchor || {};
  const [linked, setLinked] = useState([]);

  useEffect(() => {
    if (!(property || contact || record || lead)) {
      setLinked([]);
      return undefined;
    }
    let cancelled = false;
    import("../../api/linkedNotes")
      .then(({ getLinkedNotes }) => getLinkedNotes({ property, contact, record, lead, from }))
      .then((data) => {
        if (!cancelled) setLinked(Array.isArray(data?.notes) ? data.notes : []);
      })
      .catch(() => {
        if (!cancelled) setLinked([]);
      });
    return () => {
      cancelled = true;
    };
  }, [property, contact, record, lead, from, refreshKey]);

  return linked;
}

export function LinkedNoteCard({ note }) {
  return (
    <div
      className="bg-white p-4 rounded-lg border-l-4 border-amber-300 border border-gray-100 shadow-sm"
      data-testid="linked-note"
    >
      <div className="flex justify-between items-start gap-2 mb-1">
        <span className="text-xs font-bold text-[#374151]">{note.author_name || "Agent"}</span>
        <span className="text-[10px] text-gray-400 flex-shrink-0">
          {note.created_at ? formatMelbourneDateTime(note.created_at) : ""}
        </span>
      </div>
      <div className="mb-2 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-amber-600">
        <Link2 className="w-3 h-3" />
        From {linkedSourceLabel(note)}
      </div>
      <p className="text-sm text-gray-600 whitespace-pre-wrap leading-relaxed">{note.content}</p>
    </div>
  );
}

/** A titled block of linked notes, for screens that list their own notes separately. */
export function LinkedNotesSection({ notes = [], className = "" }) {
  if (!notes.length) return null;
  return (
    <div className={className} data-testid="linked-notes-section">
      <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
        From elsewhere in the CRM ({notes.length})
      </h4>
      <div className="space-y-2">
        {notes.map((n) => (
          <LinkedNoteCard key={n.key} note={n} />
        ))}
      </div>
    </div>
  );
}
